import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient } from 'react-query';

import { useUserId } from '../../../app/hooks/accounts';
import { useDeleteCollection, useGetCollection, usePatchCollection } from '../../../app/hooks/view';
import { useTabManager } from '../../context/tab-manager-context';

import { ConfirmCloseModal } from './ConfirmCloseModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { SaveCollectionModal } from './SaveCollectionModal';
import { ShareCollectionLinkModal } from './ShareCollectionLinkModal';
import { Tab } from './TabOptions';
import { ViewTooltipComponent } from './ViewTooltipComponent';

import { Dropdown, Icon, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { colors } from '@controlrooms/design-tokens';

export const CollectionTab = () => {
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  const { tabs, viewCollection, setViewCollection, closeAllTabs, closeCollection } =
    useTabManager();
  const viewIds = tabs.map((t) => t.id);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUserId = useUserId();
  const queryClient = useQueryClient();

  const [showManageCollectionModal, setShowManageCollectionModal] = useState(false);

  const { mutateAsync: patchCollection } = usePatchCollection();
  const { mutateAsync: deletePersistedView } = useDeleteCollection();
  const { data: currentCollection } = useGetCollection(viewCollection.collectionId as string);

  // Handle click outside for portal dropdown
  useEffect(() => {
    if (!openSubMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on the dropdown content
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on the trigger
      if (triggerRef.current && triggerRef.current.contains(target)) {
        return;
      }

      // Close if clicking anywhere else
      setOpenSubMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openSubMenu]);

  const handlePatch = useCallback(async () => {
    const collection_id = viewCollection.collectionId;
    const nonDefaultViewIds = viewIds.filter((id) => id !== 'default');
    const currentCollectionViewIds =
      currentCollection?.views?.map((v) => v.view_id as string) || [];

    const newViewIds = nonDefaultViewIds.filter((id) => !currentCollectionViewIds.includes(id));
    const deletedViewIds = currentCollectionViewIds.filter((id) => !nonDefaultViewIds.includes(id));
    try {
      if (collection_id) {
        try {
          await patchCollection({
            collection_id: collection_id,
            collection: {
              collection_name: viewCollection.name || '',
              description: viewCollection.description || '',
              add_views: newViewIds,
              remove_views: deletedViewIds,
            },
          });
          queryClient.refetchQueries(['user-collections', currentUserId]);
          queryClient.refetchQueries(['collection', collection_id]);
          setViewCollection((prev) => ({ ...prev, isDirty: false }));
        } catch (error) {
          console.error(error);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setOpenSubMenu(false);
    }
  }, [
    currentCollection?.views,
    currentUserId,
    patchCollection,
    queryClient,
    setViewCollection,
    viewCollection.collectionId,
    viewCollection.description,
    viewCollection.name,
    viewIds,
  ]);

  const handleDelete = useCallback(async () => {
    const collection_id = viewCollection.collectionId;
    if (collection_id) {
      await deletePersistedView(collection_id);
    }
  }, [deletePersistedView, viewCollection.collectionId]);

  if (!viewCollection.name || viewCollection.name === '') {
    return null;
  }

  return (
    <Tab isActive={false}>
      <div className="tab-content" style={{ paddingLeft: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Icon name={ICONS.Folder} width="15" height="20" />
          <Tooltip
            label={
              <ViewTooltipComponent
                name={viewCollection.name}
                description={viewCollection.description || ''}
                createdBy={viewCollection.createdBy || ''}
              />
            }
            theme={'dark'}
          >
            <span className="label">
              {viewCollection.isDirty && (
                <span style={{ color: colors.datavis['pink-2'], marginRight: '5px' }}>*</span>
              )}
              {viewCollection.name}
            </span>
          </Tooltip>
        </div>
        <div ref={triggerRef} style={{ position: 'relative' }}>
          <Dropdown.Root isOpen={openSubMenu}>
            <Dropdown.Trigger
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (openSubMenu) {
                  setOpenSubMenu(false);
                } else {
                  const target = e.currentTarget as HTMLElement;
                  const rect = target.getBoundingClientRect();
                  setDropdownPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                  });
                  setOpenSubMenu(true);
                }
              }}
            >
              <Icon name={ICONS.Chevron} width="10" color="#A0A38D" />
            </Dropdown.Trigger>
            {openSubMenu &&
              dropdownPosition &&
              ReactDOM.createPortal(
                <div ref={dropdownRef}>
                  <Dropdown.Options
                    position="right"
                    open={openSubMenu}
                    width="170px"
                    style={{
                      position: 'absolute',
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                      zIndex: 9999,
                    }}
                  >
                    <Dropdown.Item
                      disabled={!viewCollection.isDirty}
                      onClick={() => {
                        if (viewCollection.isDirty) {
                          handlePatch();
                          setOpenSubMenu(false);
                        }
                      }}
                    >
                      Save Changes
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setShowManageCollectionModal(true);
                        setOpenSubMenu(false);
                      }}
                    >
                      Manage
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        setConfirmDeleteModal(true);
                        setOpenSubMenu(false);
                      }}
                    >
                      Delete
                    </Dropdown.Item>
                    <Dropdown.Separator />
                    <Dropdown.Item
                      onClick={() => {
                        setConfirmCloseModal(true);
                        setOpenSubMenu(false);
                      }}
                    >
                      Close
                    </Dropdown.Item>
                    {!!viewCollection?.viewsIds.length && (
                      <Dropdown.Item
                        onClick={() => {
                          setShowShareModal(true);
                          setOpenSubMenu(false);
                        }}
                      >
                        Share
                      </Dropdown.Item>
                    )}
                  </Dropdown.Options>
                </div>,
                document.body,
              )}
          </Dropdown.Root>
        </div>
      </div>
      {showShareModal && !!viewCollection?.viewsIds.length && (
        <ShareCollectionLinkModal
          onClose={() => {
            setShowShareModal(false);
          }}
        />
      )}
      {showManageCollectionModal ? (
        <SaveCollectionModal
          onClose={() => setShowManageCollectionModal(false)}
          isUpdateCollection={true}
        />
      ) : null}
      {confirmCloseModal && (
        <ConfirmCloseModal
          onConfirm={async (state) => {
            if (state) {
              closeAllTabs();
              closeCollection();
            }
            setConfirmCloseModal(false);
          }}
        />
      )}
      {confirmDeleteModal && (
        <ConfirmDeleteModal
          onConfirm={async (state) => {
            setConfirmDeleteModal(false);
            if (state) {
              // Close immediately - UI updates right away
              closeAllTabs();
              closeCollection();
              // Then perform deletion in background
              try {
                await handleDelete();
              } catch (error) {
                console.error('Failed to delete collection:', error);
                // If deletion fails, we've already closed, which is fine
              }
            }
          }}
        />
      )}
    </Tab>
  );
};
